import { useCallback, useEffect } from 'react';
import {
  useCreatePrizeWithCodeMutation,
  useCreatePrizeWithoutCodeMutation,
  useUpdatePrizeWithCodeMutation,
  useUpdatePrizeWithoutCodeMutation,
  SeasonViewDocument,
} from '../../../../../../schema';
import { toast } from 'react-toastify';
import { FORM_ERROR, SubmissionErrors } from 'final-form';
import { Prize, PrizeFormValues } from './types';

type Props = {
  seasonId: string;
  prize?: Prize | null;
  withCode: boolean;
  onClose: () => void;
};

export const usePrizeForm = ({ seasonId, prize, withCode, onClose }: Props) => {
  const isEdit = !!prize;

  const [
    createPrizeWithCode,
    { data: createWithCodeData, loading: createWithCodeLoading },
  ] = useCreatePrizeWithCodeMutation();
  const [
    createPrizeWithoutCode,
    { data: createWithoutCodeData, loading: createWithoutCodeLoading },
  ] = useCreatePrizeWithoutCodeMutation();
  const [
    updatePrizeWithCode,
    { data: updateWithCodeData, loading: updateWithCodeLoading },
  ] = useUpdatePrizeWithCodeMutation();
  const [
    updatePrizeWithoutCode,
    { data: updateWithoutCodeData, loading: updateWithoutCodeLoading },
  ] = useUpdatePrizeWithoutCodeMutation();

  const loading =
    createWithCodeLoading ||
    createWithoutCodeLoading ||
    updateWithCodeLoading ||
    updateWithoutCodeLoading;

  const initialValues: PrizeFormValues = {
    imageId: prize?.image?.id ?? '',
    title: prize?.title ?? '',
    price: prize?.price ?? '',
    description: prize?.description ?? '',
    isPremium: prize?.isPremium ?? false,
    promoCode: prize?.__typename === 'PrizeWithCode' ? prize.promoCode : '',
    siteUrl: prize?.__typename === 'PrizeWithCode' ? prize.siteUrl : '',
  };

  const onSubmit = useCallback(
    async (values: PrizeFormValues) => {
      try {
        const price = typeof values.price === 'string' ? parseInt(values.price, 10) : values.price;

        if (isEdit && prize) {
          if (withCode) {
            await updatePrizeWithCode({
              variables: {
                input: {
                  id: prize.id,
                  imageId: values.imageId,
                  title: values.title,
                  price,
                  description: values.description,
                  isPremium: values.isPremium,
                  promoCode: values.promoCode || '',
                  siteUrl: values.siteUrl || '',
                },
              },
              refetchQueries: [{ query: SeasonViewDocument, variables: { id: seasonId } }],
            });
          } else {
            await updatePrizeWithoutCode({
              variables: {
                input: {
                  id: prize.id,
                  imageId: values.imageId,
                  title: values.title,
                  price,
                  description: values.description,
                  isPremium: values.isPremium,
                },
              },
              refetchQueries: [{ query: SeasonViewDocument, variables: { id: seasonId } }],
            });
          }
        } else {
          if (withCode) {
            await createPrizeWithCode({
              variables: {
                input: {
                  seasonId,
                  imageId: values.imageId,
                  title: values.title,
                  price,
                  description: values.description,
                  isPremium: values.isPremium,
                  promoCode: values.promoCode || '',
                  siteUrl: values.siteUrl || '',
                },
              },
              refetchQueries: [{ query: SeasonViewDocument, variables: { id: seasonId } }],
            });
          } else {
            await createPrizeWithoutCode({
              variables: {
                input: {
                  seasonId,
                  imageId: values.imageId,
                  title: values.title,
                  price,
                  description: values.description,
                  isPremium: values.isPremium,
                },
              },
              refetchQueries: [{ query: SeasonViewDocument, variables: { id: seasonId } }],
            });
          }
        }
      } catch {
        toast.error(isEdit ? 'Ошибка сохранения приза' : 'Ошибка создания приза');
        return {
          [FORM_ERROR]: isEdit ? 'Ошибка сохранения' : 'Ошибка создания',
        } as SubmissionErrors;
      }
    },
    [
      isEdit,
      prize,
      withCode,
      seasonId,
      createPrizeWithCode,
      createPrizeWithoutCode,
      updatePrizeWithCode,
      updatePrizeWithoutCode,
    ]
  );

  useEffect(() => {
    const createSuccess =
      createWithCodeData?.createPrizeWithCode.prize ||
      createWithoutCodeData?.createPrizeWithoutCode.prize;

    if (createSuccess) {
      toast.success('Приз успешно создан');
      onClose();
    }
  }, [createWithCodeData, createWithoutCodeData, onClose]);

  useEffect(() => {
    const updateSuccess =
      updateWithCodeData?.updatePrizeWithCode.prize ||
      updateWithoutCodeData?.updatePrizeWithoutCode.prize;

    if (updateSuccess) {
      toast.success('Приз успешно сохранён');
      onClose();
    }
  }, [updateWithCodeData, updateWithoutCodeData, onClose]);

  return { initialValues, onSubmit, loading, isEdit };
};
